import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import SEO from '../components/shared/SEO';
import SectionLabel from '../components/shared/SectionLabel';
import '../styles/jobspage.css';

const API_BASE = import.meta.env.VITE_API_URL || 'https://apex-corporateweb-470928162af3.herokuapp.com';

interface JobSection {
  type: 'job';
  id: string;
  title: string;
  department: string;
  location: string;
  employmentType: string;
  description: string;
  applyUrl?: string;
}

interface HeroSection {
  type: 'hero';
  headline: string;
  subheadline: string;
  body?: string;
}

interface JobsPageData {
  slug: string;
  title: string;
  status: string;
  sections: (HeroSection | JobSection)[];
  seo: { title?: string; description?: string };
}

function fetchJobsPage(): Promise<JobsPageData> {
  return fetch(`${API_BASE}/api/pages/jobs`)
    .then((r) => {
      if (!r.ok) throw new Error('not_found');
      return r.json();
    })
    .then((r) => r.data);
}

export default function JobsPage() {
  const { data: page, isLoading, isError } = useQuery({
    queryKey: ['page', 'jobs'],
    queryFn: fetchJobsPage,
    retry: false,
  });

  const hero = page?.sections.find((s): s is HeroSection => s.type === 'hero');
  const jobs = page?.sections.filter((s): s is JobSection => s.type === 'job') ?? [];

  return (
    <Layout>
      <SEO
        title={page?.seo?.title ?? 'Careers — Apex DeFi Labs Inc.'}
        description={page?.seo?.description ?? 'Explore open positions at Apex DeFi Labs Inc..'}
        url="https://apexdefilabs.com/jobs"
        type="website"
        keywords="Apex DeFi Labs Inc. careers, jobs, hiring, blockchain jobs, DeFi careers"
      />

      <div className="jobs-page">
        {/* Hero */}
        <section className="jobs-hero">
          <div className="jobs-hero-overlay" />
          <div className="jobs-shell">
            {isLoading ? (
              <div className="jobs-hero-loading">
                <div className="jobs-skeleton jobs-skeleton--title" />
                <div className="jobs-skeleton jobs-skeleton--sub" />
              </div>
            ) : (
              <div className="jobs-hero-content">
                <SectionLabel>Careers</SectionLabel>
                <h1 className="jobs-hero-title">
                  {hero?.headline ?? 'Build the Future With Us'}
                </h1>
                <p className="jobs-hero-sub">
                  {hero?.subheadline ?? "We're always looking for exceptional people to join the Apex team."}
                </p>
                {hero?.body && <p className="jobs-hero-body">{hero.body}</p>}
              </div>
            )}
          </div>
        </section>

        {/* Positions */}
        <section className="jobs-positions">
          <div className="jobs-shell">
            {isLoading ? (
              <div className="jobs-list">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="jobs-card jobs-card--loading">
                    <div className="jobs-skeleton jobs-skeleton--card-title" />
                    <div className="jobs-skeleton jobs-skeleton--card-sub" />
                    <div className="jobs-skeleton jobs-skeleton--card-body" />
                  </div>
                ))}
              </div>
            ) : isError || jobs.length === 0 ? (
              <div className="jobs-empty">
                <div className="jobs-empty-icon">◎</div>
                <h2 className="jobs-empty-title">No Open Positions</h2>
                <p className="jobs-empty-body">
                  We don't have any open roles right now, but we're always interested in exceptional talent.
                </p>
                <Link to="/contact" className="jobs-empty-cta">Get In Touch →</Link>
              </div>
            ) : (
              <>
                <div className="jobs-section-header">
                  <SectionLabel>Open Positions</SectionLabel>
                  <h2 className="jobs-section-title">
                    Join <em>Our Team</em>
                  </h2>
                </div>

                <div className="jobs-list">
                  {jobs.map((job) => (
                    <div key={job.id} className="jobs-card">
                      <div className="jobs-card-header">
                        <div className="jobs-card-meta">
                          {job.department && (
                            <span className="jobs-card-dept">{job.department}</span>
                          )}
                          <h3 className="jobs-card-title">{job.title}</h3>
                          <div className="jobs-card-tags">
                            {job.location && (
                              <span className="jobs-card-tag">
                                <span className="jobs-tag-icon">◎</span>
                                {job.location}
                              </span>
                            )}
                            {job.employmentType && (
                              <span className="jobs-card-tag">{job.employmentType}</span>
                            )}
                          </div>
                        </div>
                        <a
                          href={job.applyUrl || '/contact'}
                          target={job.applyUrl ? '_blank' : undefined}
                          rel={job.applyUrl ? 'noreferrer' : undefined}
                          className="jobs-apply-btn"
                        >
                          Apply Now
                        </a>
                      </div>

                      {job.description && (
                        <div
                          className="jobs-card-desc"
                          dangerouslySetInnerHTML={{ __html: job.description }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* CTA strip */}
        {!isLoading && (
          <section className="jobs-cta">
            <div className="jobs-shell">
              <div className="jobs-cta-inner">
                <div className="jobs-cta-text">
                  <p className="jobs-cta-label">Don't see the right role?</p>
                  <p className="jobs-cta-sub">Send us your profile — we're always open to exceptional candidates.</p>
                </div>
                <Link to="/contact" className="jobs-cta-btn">Contact Us</Link>
              </div>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
